import { playerRequest, completeUpload, getUploadPartUrl, getUploadGroupDefaults, initiateUpload, listUserGroups } from '../service'
import React, { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import LoadingButton from '@mui/lab/LoadingButton'
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded'
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded'
import MovieCreationOutlinedIcon from '@mui/icons-material/MovieCreationOutlined'
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import axios from 'axios'
import GroupOptions from '../components/common/GroupOptions'
import Messages from '../components/common/Messages'
import { DateTime } from 'luxon'
import { Link as RouterLink } from 'react-router-dom'
import { normalizePlayerPosition, PLAYER_POSITION_OPTIONS } from '../constants/playerPositions'
import { STARTING_SIDE_OPTIONS } from '../constants/gameAppearance'
import { TERMS_PATH, UPLOAD_ATTESTATION, UPLOAD_ATTESTATION_NEEDED, UPLOAD_DISCLAIMER } from '../constants/legal'
import { apiErrorText } from '../service/coaching'
import { describeGap, formatDuration, gapsBetween, orderCheck, readDuration, recordingTime, sortByRecording } from '../service/uploadOrder'

// The API's own sentence (for example, that the Terms of Use changed), else the error as before.
const errorText = err => apiErrorText(err, err?.message || String(err))

const queuedFile = (file, index) => ({
  id: `${file.name}-${file.size}-${file.lastModified}-${index}`,
  file,
  status: 'queued',
  progress: 0,
  uploadId: null,
  error: null,
  // Read from the file's metadata after it is added; null when the browser can't tell.
  durationSeconds: undefined
})

// Only AI analysis uses this: edited footage can jump across cuts, so a later moment is not
// evidence about an earlier one. Shift clips and highlights are analysed the same way.
const CONTENT_TYPES = [
  { value: 'whole_game', label: 'Continuous recording', description: 'A game, practice or period recorded without cuts.' },
  { value: 'highlight_clip', label: 'Edited clips or highlights', description: 'Shifts, highlights or a compilation with cuts between clips.' }
]

const MAX_JOINED_FILES = 20

const formatBytes = bytes => {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const unit = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  return `${(bytes / Math.pow(1024, unit)).toFixed(unit >= 2 ? 1 : 0)} ${units[unit]}`
}

function UploadFile () {
  const [players, setPlayers] = useState([])
  const [targets, setTargets] = useState([])
  // Paid analysis starts only on this explicit request, never because players were selected.
  const [autoAnalyze, setAutoAnalyze] = useState(false)
  const analyzeAfterProcessing = autoAnalyze && targets.length > 0
  const [contentType, setContentType] = useState('whole_game')
  // The footage hint matters only to analysis, so it is asked (and sent) only with players.
  const sourceType = targets.length ? contentType : 'whole_game'
  const [playerError, setPlayerError] = useState('')
  const [gameId, setGameId] = useState(null)
  const [name, setName] = useState('')
  useEffect(() => {
    playerRequest('/players').then(setPlayers).catch(e => setPlayerError(e.message))
  }, [])
  const [message, setMessage] = useState(null)
  const [dateTime, setDateTime] = useState(DateTime.now())
  // Until the owner picks a date, it follows the first file's recording time.
  const [dateTouched, setDateTouched] = useState(false)
  const [dateFromFile, setDateFromFile] = useState(false)
  const [description, setDescription] = useState('')
  const [files, setFiles] = useState([])
  const [combinedUploadId, setCombinedUploadId] = useState(null)
  // Several files are always joined into one upload (D34, D35); the order decides the timeline.
  const joinFiles = files.length > 1
  const [manualOrder, setManualOrder] = useState(false)
  const [orderConfirmed, setOrderConfirmed] = useState(false)
  const fileComponent = useRef(null)
  const durationRequests = useRef(new Set())
  const [groups, setGroups] = useState([])
  const [selectedGroups, setSelectedGroups] = useState([])
  const [loading, setLoading] = useState(false)
  const [rightsConfirmed, setRightsConfirmed] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const locked = loading || Boolean(gameId)

  useEffect(() => {
    let cancelled = false
    const loadGroups = async () => {
      try {
        const available = await listUserGroups({ writable: true })
        const defaults = await getUploadGroupDefaults()
        if (!cancelled) {
          setGroups(available || [])
          setSelectedGroups(defaults || [])
        }
      } catch (err) {
        console.error(err)
      }
    }
    loadGroups()
    return () => {
      cancelled = true
    }
  }, [])

  const updateFileState = (id, patch) => {
    setFiles(current => current.map(item => (
      item.id === id ? { ...item, ...patch } : item
    )))
  }

  // Each file's length, for the gaps between recordings; read once per file.
  useEffect(() => {
    files.forEach(item => {
      if (item.durationSeconds !== undefined || durationRequests.current.has(item.id)) return
      durationRequests.current.add(item.id)
      readDuration(item.file).then(seconds => updateFileState(item.id, { durationSeconds: seconds }))
    })
  }, [files])

  const followFirstRecording = ordered => {
    if (dateTouched || !ordered.length) return
    const recorded = recordingTime(ordered[0].file.name)
    if (recorded !== null) {
      setDateTime(DateTime.fromMillis(recorded))
      setDateFromFile(true)
    }
  }

  const setOrder = (next, { manual = false } = {}) => {
    setFiles(next)
    setManualOrder(manual)
    setOrderConfirmed(false)
    followFirstRecording(next)
  }

  const addFiles = selected => {
    if (locked) return
    const videos = Array.from(selected || []).filter(file => file.type.startsWith('video/'))
    const existing = new Set(files.map(item => `${item.file.name}:${item.file.size}:${item.file.lastModified}`))
    const additions = videos
      .filter(file => !existing.has(`${file.name}:${file.size}:${file.lastModified}`))
      .map((file, index) => queuedFile(file, files.length + index))
    if (!additions.length) return
    const combined = files.concat(additions)
    // A hand-made order is kept; new files then go last and the order check flags them.
    setOrder(manualOrder ? combined : sortByRecording(combined), { manual: manualOrder })
  }

  const fileChanged = ev => {
    addFiles(ev.target.files)
    ev.target.value = ''
  }

  const handleDrop = ev => {
    ev.preventDefault()
    setDragActive(false)
    addFiles(ev.dataTransfer.files)
  }

  const onGroupChange = values => {
    setSelectedGroups(values)
  }

  const removeFile = id => {
    setOrder(files.filter(item => item.id !== id), { manual: manualOrder })
  }

  const moveFile = (index, direction) => {
    if (locked) return
    const target = index + direction
    if (target < 0 || target >= files.length) return
    const next = [...files]
    ;[next[index], next[target]] = [next[target], next[index]]
    setOrder(next, { manual: true })
  }

  const order = orderCheck(files)
  const gaps = gapsBetween(files)
  // Joining in the wrong order means processing again, so an order the names can't confirm
  // needs the owner's check before upload.
  const needsOrderConfirmation = joinFiles && !(order.verifiable && order.inOrder)
  const tooManyFiles = files.length > MAX_JOINED_FILES

  const uploadCombined = async currentGameId => {
    let uploadId = combinedUploadId
    try {
      if (uploadId) {
        // A failed HTTP response can still mean the file reached storage.
        // Reconcile before retrying so an upload that is already processing is recovered.
        const existing = await completeUpload(uploadId).catch(() => null)
        if (existing && ['queued', 'submitting', 'processing', 'ready'].includes(existing.status)) {
          files.forEach(item => updateFileState(item.id, { status: 'complete', progress: 100, error: null }))
          setMessage({ text: `${files.length} files uploaded and queued as one video.`, timeout: -1 })
          return
        }
      }
      if (!uploadId) {
        const initiated = await initiateUpload({
          name: name.trim(),
          gameId: currentGameId,
          date: dateTime.toJSDate(),
          description: description || undefined,
          file: files[0].file,
          files: files.map(item => item.file),
          groups: selectedGroups,
          sourceType,
          sequenceIndex: null
        })
        uploadId = initiated.upload.id
        setCombinedUploadId(uploadId)
      }
      let failed = 0
      for (let index = 0; index < files.length; index++) {
        const item = files[index]
        if (item.status === 'complete') continue
        try {
          updateFileState(item.id, { status: 'preparing', progress: 0, error: null, uploadId })
          // Obtain a fresh URL for each transfer, even when earlier files took
          // longer than the signed URL lifetime or this is a failed-file retry.
          const { upload_url: uploadUrl } = await getUploadPartUrl(uploadId, index + 1)
          updateFileState(item.id, { status: 'uploading' })
          await axios.put(uploadUrl, item.file, {
            headers: { 'Content-Type': item.file.type || 'video/quicktime' },
            onUploadProgress: progressEvent => {
              if (progressEvent.total) {
                updateFileState(item.id, {
                  progress: Math.min(100, Math.round(progressEvent.loaded * 100 / progressEvent.total))
                })
              }
            }
          })
          updateFileState(item.id, { status: 'complete', progress: 100 })
        } catch (err) {
          failed += 1
          updateFileState(item.id, { status: 'error', error: errorText(err) })
        }
      }
      if (failed) {
        setMessage({ text: `${failed} file${failed === 1 ? '' : 's'} failed. Retry to upload only the failed files. The video will wait for every file.`, severity: 'warning', timeout: -1 })
        return
      }
      await completeUpload(uploadId)
      setMessage({ text: `${files.length} files uploaded and queued as one video.`, timeout: -1 })
    } catch (err) {
      setMessage({ text: `${errorText(err)} Retry to continue the same upload.`, severity: 'error', timeout: -1 })
    } finally {
      setLoading(false)
    }
  }

  const uploadSingle = async currentGameId => {
    const item = files[0]
    if (item.status === 'complete') {
      setLoading(false)
      return
    }
    try {
      updateFileState(item.id, { status: 'preparing', progress: 0, error: null })
      const initiated = await initiateUpload({
        name: name.trim(),
        gameId: currentGameId,
        date: dateTime && dateTime.toJSDate ? dateTime.toJSDate() : new Date(dateTime),
        description: description || undefined,
        file: item.file,
        groups: selectedGroups,
        sourceType,
        sequenceIndex: null
      })
      updateFileState(item.id, { status: 'uploading', uploadId: initiated.upload.id })
      await axios.put(initiated.uploadUrl, item.file, {
        headers: { 'Content-Type': item.file.type || 'video/quicktime' },
        onUploadProgress: progressEvent => {
          if (!progressEvent.total) return
          updateFileState(item.id, {
            progress: Math.min(100, Math.round((progressEvent.loaded * 100) / progressEvent.total))
          })
        }
      })
      let confirmationDelayed = false
      try {
        await completeUpload(initiated.upload.id)
      } catch (confirmationError) {
        console.warn('Upload reached S3 but server confirmation was delayed.', confirmationError)
        confirmationDelayed = true
      }
      updateFileState(item.id, { status: 'complete', progress: 100 })
      setMessage(confirmationDelayed
        ? { text: '1 video reached storage. Server confirmation is delayed; the watchdog will reconcile it automatically.', severity: 'warning', timeout: -1 }
        : { text: '1 video uploaded successfully and queued for processing.', timeout: -1 })
    } catch (err) {
      console.error(err)
      updateFileState(item.id, { status: 'error', error: errorText(err) })
      setMessage({ text: 'The video didn’t upload. The reason is shown with the file; retry when it’s fixed.', severity: 'error', timeout: -1 })
    } finally {
      setLoading(false)
    }
  }

  const readyToUpload = Boolean(name.trim() && files.length && rightsConfirmed && !loading && !tooManyFiles &&
    (!needsOrderConfirmation || orderConfirmed))

  const handleUpload = async () => {
    // The API requires and records the rights confirmation that initiateUpload sends.
    if (!readyToUpload) return
    if (targets.some(target => !/^\d{0,3}$/.test(target.jersey || ''))) {
      setMessage({ text: 'Jersey numbers can have up to three digits.', severity: 'warning', timeout: -1 })
      return
    }
    // Automatic analysis needs the player's appearance in this video; storage and later analysis do not.
    if (analyzeAfterProcessing && targets.some(target => !target.jersey || !target.uniform?.trim())) {
      setMessage({ text: 'Confirm the jersey number and uniform color for each selected player in this video.', severity: 'warning', timeout: -1 })
      return
    }

    setLoading(true)
    let currentGameId = gameId
    try {
      if (!currentGameId) {
        // The game record holds the date, players and analysis settings for this upload.
        const game = await playerRequest('/games', 'POST', {
          name: name.trim(),
          recorded_at: dateTime.toJSDate(),
          auto_analyze: analyzeAfterProcessing,
          players: targets.map(({ id, team, jersey, uniform, position, starting_side: startingSide }) => ({ player_id: id, team, jersey, uniform: uniform || '', position: normalizePlayerPosition(position) || 'unknown', starting_side: startingSide || 'unknown' }))
        })
        currentGameId = game.id
        setGameId(game.id)
      }
    } catch (err) {
      setMessage({ text: err.message, severity: 'error', timeout: -1 }); setLoading(false); return
    }
    if (joinFiles) await uploadCombined(currentGameId)
    else await uploadSingle(currentGameId)
  }

  const resetForm = () => {
    setGameId(null)
    setCombinedUploadId(null)
    setTargets([])
    setAutoAnalyze(false)
    setContentType('whole_game')
    setName('')
    setDateTime(DateTime.now())
    setDateTouched(false)
    setDateFromFile(false)
    setDescription('')
    setFiles([])
    setManualOrder(false)
    setOrderConfirmed(false)
    setSelectedGroups([])
    setRightsConfirmed(false)
    setMessage(null)
  }

  const completed = files.length > 0 && files.every(item => item.status === 'complete')

  const orderNotice = () => {
    if (!joinFiles || tooManyFiles) return null
    if (order.verifiable && order.inOrder) {
      return <Alert severity="success">In recording order, from the times in the file names. Check the times and gaps below.</Alert>
    }
    if (order.verifiable) {
      return (
        <Alert severity="warning" action={<Button color="inherit" size="small" disabled={locked} onClick={() => setOrder(sortByRecording(files))}>Sort by recording time</Button>}>
          File {order.outOfOrderAt + 1} was recorded before file {order.outOfOrderAt}. Joined in this order, the video would jump back in time.
        </Alert>
      )
    }
    return (
      <Alert severity="warning" action={manualOrder ? <Button color="inherit" size="small" disabled={locked} onClick={() => setOrder(sortByRecording(files))}>Sort by name</Button> : undefined}>
        These file names don’t include recording times, so the order can’t be checked automatically. {manualOrder ? 'Check your order' : 'They are sorted by name; check the order'} before uploading. Fixing a wrong order later means processing the video again.
      </Alert>
    )
  }

  return (
    <>
      <Box data-testid="upload-workspace" sx={{ maxWidth: 1180, mx: 'auto', px: { xs: 1, sm: 2 }, py: 2 }}>
        <Stack spacing={3}>
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <VideoLibraryOutlinedIcon color="primary" sx={{ fontSize: 34 }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  Upload video
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  One video file, or several recordings joined into one video.
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="overline" color="primary.light" sx={{ letterSpacing: '.14em' }}>
                  1 · FILES
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose one file, or every recording of the same event. Several files are joined into one video in the order shown.
                </Typography>
              </Box>

              <Box
                onDragEnter={ev => {
                  ev.preventDefault()
                  if (!loading) setDragActive(true)
                }}
                onDragOver={ev => ev.preventDefault()}
                onDragLeave={() => setDragActive(false)}
                onDrop={handleDrop}
                sx={{
                  border: '2px dashed',
                  borderColor: dragActive ? 'primary.main' : 'divider',
                  borderRadius: 3,
                  px: 3,
                  py: 4,
                  textAlign: 'center',
                  bgcolor: dragActive ? 'rgba(56,165,198,.08)' : 'rgba(255,255,255,.015)',
                  transition: '150ms ease'
                }}
              >
                <Stack spacing={1.25} alignItems="center">
                  <CloudUploadRoundedIcon color="primary" sx={{ fontSize: 46 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Drop video files here
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Files with the recording time in their names (most rink cameras and phones) are put in order automatically.
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    disabled={locked}
                    startIcon={<MovieCreationOutlinedIcon />}
                  >
                    Choose videos
                    <input
                      id="file"
                      ref={fileComponent}
                      hidden
                      multiple
                      accept="video/*"
                      type="file"
                      onChange={fileChanged}
                    />
                  </Button>
                </Stack>
              </Box>

              {tooManyFiles && <Alert severity="error">Join up to {MAX_JOINED_FILES} files into one video. Remove some files.</Alert>}
              {orderNotice()}

              {files.length
                ? <Stack spacing={1.25}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {joinFiles ? 'Join order' : 'File'}
                      </Typography>
                      <Chip
                        size="small"
                        label={joinFiles ? `${files.length} files → 1 video` : '1 file'}
                        color="primary"
                        variant="outlined"
                      />
                    </Stack>

                    {files.map((item, index) => {
                      const statusIcon = item.status === 'complete'
                        ? <CheckCircleRoundedIcon color="success" />
                        : item.status === 'error'
                          ? <ErrorOutlineRoundedIcon color="error" />
                          : <MovieCreationOutlinedIcon color="action" />
                      const recorded = recordingTime(item.file.name)
                      const facts = [
                        formatBytes(item.file.size),
                        item.durationSeconds === undefined
                          ? 'Reading length…'
                          : item.durationSeconds ? formatDuration(item.durationSeconds) : 'Length unknown',
                        recorded !== null ? `Recorded ${DateTime.fromMillis(recorded).toFormat('LLL d, h:mm:ss a')}` : null
                      ].filter(Boolean)
                      const gap = index < files.length - 1 ? describeGap(gaps[index]) : null
                      const outOfOrder = order.outOfOrderAt === index

                      return (
                        <React.Fragment key={item.id}>
                          <Paper variant="outlined" sx={{ p: 1.75, bgcolor: 'rgba(255,255,255,.015)', borderColor: outOfOrder ? 'warning.main' : undefined }}>
                            <Grid container spacing={1.5} alignItems="center">
                              <Grid item xs="auto">
                                {statusIcon}
                              </Grid>
                              {joinFiles && <Grid item xs="auto">
                                <Typography variant="subtitle2" color="text.secondary" aria-label={`File ${index + 1} of ${files.length}`}>{index + 1}</Typography>
                              </Grid>}
                              <Grid item xs={12} sm>
                                <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                                  {item.file.name}
                                </Typography>
                                <Typography variant="caption" color={outOfOrder ? 'warning.main' : 'text.secondary'}>
                                  {facts.join(' · ')}{outOfOrder ? ' · recorded before the file above' : ''}
                                </Typography>
                              </Grid>
                              <Grid item xs="auto">
                                {joinFiles && <>
                                  <IconButton aria-label={`Move ${item.file.name} earlier`} disabled={locked || index === 0} onClick={() => moveFile(index, -1)}><ArrowUpwardRoundedIcon /></IconButton>
                                  <IconButton aria-label={`Move ${item.file.name} later`} disabled={locked || index === files.length - 1} onClick={() => moveFile(index, 1)}><ArrowDownwardRoundedIcon /></IconButton>
                                </>}
                                <IconButton
                                  aria-label={`Remove ${item.file.name}`}
                                  disabled={locked}
                                  onClick={() => removeFile(item.id)}
                                >
                                  <DeleteOutlineRoundedIcon />
                                </IconButton>
                              </Grid>
                              {(item.status === 'preparing' || item.status === 'uploading' || item.status === 'complete')
                                ? <Grid item xs={12}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                      <LinearProgress
                                        variant="determinate"
                                        value={item.status === 'preparing' ? 2 : item.progress}
                                        sx={{ flex: 1, height: 7, borderRadius: 999 }}
                                      />
                                      <Typography variant="caption" color="text.secondary" sx={{ width: 72, textAlign: 'right' }}>
                                        {item.status === 'preparing'
                                          ? 'Preparing'
                                          : item.status === 'complete'
                                            ? 'Uploaded'
                                            : `${item.progress}%`}
                                      </Typography>
                                    </Stack>
                                  </Grid>
                                : null}
                              {item.status === 'error'
                                ? <Grid item xs={12}>
                                    <Alert severity="error">
                                      {item.error || 'Upload failed.'}
                                    </Alert>
                                  </Grid>
                                : null}
                            </Grid>
                          </Paper>
                          {gap && <Typography variant="caption" sx={{ pl: 2 }} color={gap.severity === 'success' ? 'success.main' : gap.severity === 'warning' ? 'warning.main' : 'text.secondary'}>
                            ↓ {gap.text}
                          </Typography>}
                        </React.Fragment>
                      )
                    })}

                    {needsOrderConfirmation && !tooManyFiles && <FormControlLabel
                      control={<Checkbox checked={orderConfirmed} disabled={locked} onChange={event => setOrderConfirmed(event.target.checked)} />}
                      label={<Typography variant="body2">I checked: these {files.length} files are in the order they were recorded.</Typography>}
                    />}
                  </Stack>
                : null}
            </Stack>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, md: 3 },
              background: 'linear-gradient(145deg, rgba(20,34,45,.96), rgba(12,20,28,.98))'
            }}
          >
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="overline" color="primary.light" sx={{ letterSpacing: '.14em' }}>
                  2 · DETAILS
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  One name and date for this upload, whatever it is: a game, a practice or anything else.
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <TextField
                    required
                    fullWidth
                    id="name"
                    label="Name"
                    placeholder="Jr Flyers vs Washington Pride, or Tuesday practice"
                    value={name}
                    disabled={locked}
                    onChange={ev => setName(ev.target.value)}
                    helperText="Use a name that will be easy to find later."
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DateTimePicker
                    sx={{ width: '100%' }}
                    slotProps={{ textField: { variant: 'outlined', fullWidth: true, helperText: dateFromFile && !dateTouched ? 'From the first file’s recording time' : undefined } }}
                    label="Date / time"
                    value={dateTime}
                    disabled={locked}
                    onChange={value => { setDateTime(value); setDateTouched(true) }}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    id="description"
                    label="Description"
                    placeholder="Tournament, rink, opponent, notes..."
                    multiline
                    rows={3}
                    value={description}
                    disabled={locked}
                    onChange={ev => setDescription(ev.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: 'rgba(255,255,255,.02)' }}>
                    {groups.length
                      ? <GroupOptions
                          groups={groups}
                          selectedGroups={selectedGroups}
                          onGroupChange={onGroupChange}
                          disabled={locked}
                        />
                      : <Stack spacing={0.5}>
                          <Typography variant="subtitle2">Sharing</Typography>
                          <Typography variant="body2" color="text.secondary">
                            This upload will be private to your account.
                          </Typography>
                        </Stack>}
                  </Paper>
                </Grid>
              </Grid>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="overline" color="primary.light" sx={{ letterSpacing: '.14em' }}>
                  3 · PLAYERS AND AI ANALYSIS (OPTIONAL)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Leave empty to store and watch the video only.
                </Typography>
              </Box>

              {playerError && <Alert severity="warning">Players could not be loaded: {playerError}</Alert>}
              <Autocomplete multiple options={players} value={targets} disabled={locked}
                getOptionLabel={player => player.name} isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(_e, values) => setTargets(values)}
                renderInput={params => <TextField {...params} label="Players to track and analyze" helperText="Selecting players does not start analysis. Choose below whether to analyze after processing, or start it later from the details page." />} />
              {!!targets.length && <TextField select label="What’s in this video?" value={contentType} disabled={locked}
                onChange={event => setContentType(event.target.value)}
                helperText={`${CONTENT_TYPES.find(type => type.value === contentType).description} Analysis uses this to know whether the footage can jump across cuts.`}>
                {CONTENT_TYPES.map(type => <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>)}
              </TextField>}
              {!!targets.length && <Box>
                <FormControlLabel label="Analyze automatically after processing (beta)" control={<Checkbox checked={autoAnalyze}
                  disabled={locked} onChange={event => setAutoAnalyze(event.target.checked)} />} />
                <FormHelperText sx={{ mt: 0 }}>Off by default. When on, AI player tracking and analysis starts for each selected player once the video is processed. Results are a beta and may be wrong. You can start or re-run analysis later without uploading again.</FormHelperText>
              </Box>}
              {!gameId && <Stack direction="row" spacing={1}>
                <Button component={RouterLink} to="/players" target="_blank" rel="noopener">Manage players in a new tab</Button>
                <Button onClick={() => playerRequest('/players').then(rows => { setPlayers(rows); setPlayerError('') }).catch(e => setPlayerError(e.message))}>Refresh players</Button>
              </Stack>}
              {targets.map(target => <Stack key={target.id} spacing={1}>
                <Typography variant="subtitle2">{target.name}: confirm their appearance in this video</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  {['team', 'jersey', 'uniform'].map(field => <TextField key={field} required={analyzeAfterProcessing && field !== 'team'} label={field === 'uniform' ? 'Uniform color and trim in this video' : `${target.name} ${field}`} value={target[field] || ''} helperText={field === 'uniform' ? 'Home, away, third or showcase uniform' : undefined} inputProps={{ maxLength: field === 'jersey' ? 3 : 200 }} disabled={locked} onChange={e => setTargets(rows => rows.map(row => row.id === target.id ? { ...row, [field]: e.target.value } : row))} />)}
                  <TextField select label="Position in this video" value={normalizePlayerPosition(target.position) || 'unknown'} disabled={locked} onChange={e => setTargets(rows => rows.map(row => row.id === target.id ? { ...row, position: e.target.value } : row))} sx={{ minWidth: 150 }}>
                    {PLAYER_POSITION_OPTIONS.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                  </TextField>
                </Stack>
                <TextField select label="Starting goal side" value={target.starting_side || 'unknown'} disabled={locked} helperText="The goal this team defends at the start of play, as seen in the video. Teams can change ends later."
                  onChange={e => setTargets(rows => rows.map(row => row.id === target.id ? { ...row, starting_side: e.target.value } : row))}>
                  {STARTING_SIDE_OPTIONS.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </TextField>
              </Stack>)}
              {analyzeAfterProcessing && <Alert severity="info">Confirm these details once for this video. Matching and analysis (beta) start automatically after processing. Results show matched player clips or guide you through correcting the details here in Highlights. An optional reference can help when a jersey is hard to read; identifying every clip is not required.</Alert>}
              {!!targets.length && !analyzeAfterProcessing && <Alert severity="info">Analysis will not start automatically. Details entered here are saved with the upload. To start analysis later from the details page, each player needs their jersey number and uniform in this video; otherwise enter them when you start analysis from the video.</Alert>}
            </Stack>
          </Paper>

          <Typography variant="body2" color="text.secondary">
            {UPLOAD_DISCLAIMER}{' '}
            <Link component={RouterLink} to={TERMS_PATH} target="_blank" rel="noopener">
              Read the Terms of Use
            </Link>
          </Typography>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              position: { md: 'sticky' },
              bottom: { md: 12 },
              zIndex: 2,
              bgcolor: 'rgba(17,24,32,.96)',
              backdropFilter: 'blur(12px)'
            }}
          >
            <FormControlLabel
              sx={{ mb: 1 }}
              control={<Checkbox checked={rightsConfirmed} disabled={loading} onChange={event => setRightsConfirmed(event.target.checked)} />}
              label={<Typography variant="body2">{UPLOAD_ATTESTATION}</Typography>}
            />
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {!files.length
                    ? 'Add at least one video file to continue'
                    : !rightsConfirmed
                        ? UPLOAD_ATTESTATION_NEEDED
                        : needsOrderConfirmation && !orderConfirmed && !tooManyFiles
                          ? 'Check the file order to continue'
                          : joinFiles ? `${files.length} files → 1 video` : '1 file → 1 video'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Files upload one at a time so each has a clear status.
                </Typography>
              </Box>

              <Stack direction="row" spacing={1}>
                {(completed || gameId)
                  ? <Button variant="outlined" disabled={loading} onClick={resetForm}>
                      Upload another video
                    </Button>
                  : null}
                <LoadingButton
                  variant="contained"
                  size="large"
                  onClick={handleUpload}
                  loading={loading}
                  disabled={!readyToUpload}
                  startIcon={<CloudUploadRoundedIcon />}
                >
                  {joinFiles ? `Upload ${files.length} files as one video` : 'Upload video'}
                </LoadingButton>
              </Stack>
            </Stack>
          </Paper>

          {gameId && <Button component={RouterLink} to={`/game/${gameId}`}>View details and processing status</Button>}
        </Stack>
      </Box>

      <Messages message={message} onClose={() => setMessage(null)} />
    </>
  )
}

export default UploadFile
